import {
  Button,
  type SetDropdownStateRef,
  TextInput,
  useValidatedState,
} from "@hoylu/client-common";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { getUserOrgId } from "../../../state/user/user.selector";
import {
  activateDashboardOption,
  editThumbnail,
  updateWorkspaceName,
} from "../../../state/workspaces/workspaces.actions";
import { selectSelectedWorkspace } from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { isRequiredValidation } from "../../../utils/is.required.validation";
import { isWithinCharLimitValidation } from "../../../utils/is.within.char.limit.validation";
import SimpleWorkspaceCard from "../../cards/SimpleWorkspaceCard";
import { ProjectDropdownWithRemoveButton } from "../../project/ProjectDropdownWithRemoveButton";
import { useAssignWorkspaceToProject } from "../../project/use.assign.workspace.to.project";
import { RemoveButton } from "../modalElements/RemoveButton";
import { ShareSection } from "./ShareSection";
import { ShareSectionHeader } from "./ShareSectionHeader";
import { InfoSection } from "./sharing.enums";
import Styles from "./WorkspaceInfoSection.module.css";

export type WorkspaceInfoSectionProps = {
  handleChangeSectionType: (section: InfoSection) => void;
  initiallyOpen: boolean;
  isOrgAdmin: boolean;
  setDropdownStateRef: React.Ref<SetDropdownStateRef>;
  shouldSelectTitle: boolean;
};

export const WorkspaceInfoSection = ({
  handleChangeSectionType,
  initiallyOpen,
  isOrgAdmin,
  setDropdownStateRef,
  shouldSelectTitle,
}: WorkspaceInfoSectionProps) => {
  const dispatch = useDispatch();

  const t = useI18n("SHARE_WORKSPACE_DIALOG.");

  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace);

  const userOrgId = useSelector(getUserOrgId);
  const showTemplateCreationUIFlag = useSelector(
    (state: RootState) =>
      state.context.config.featureFlags.showTemplateCreationUI,
  );
  const showOrgTemplateCreationUIFlag = useSelector(
    (state: RootState) => state.context.config.featureFlags.orgTemplates,
  );

  const { currentProjects, projectOptions, setCurrentProjects } =
    useAssignWorkspaceToProject(selectedMyWorkspaceMetadata);

  const titleInputRef = useRef<HTMLInputElement>(null);

  const [isNameEdited, setIsNameEdited] = useState(false);
  const [wasNameSend, setWasNameSend] = useState(false);
  const [isOpenSection, setIsOpenSection] = useState(initiallyOpen);

  const [workspaceName, setWorkspaceName, nameError] = useValidatedState<
    string | undefined
  >(selectedMyWorkspaceMetadata?.workspaceName ?? "", [
    isRequiredValidation,
    isWithinCharLimitValidation,
  ]);

  const nameHasChanged = useMemo(
    () => selectedMyWorkspaceMetadata?.workspaceName !== workspaceName,
    [selectedMyWorkspaceMetadata?.workspaceName, workspaceName],
  );

  const isWorkspaceAdmin =
    !!selectedMyWorkspaceMetadata?.myPermissions.canAdmin;

  const showPublishAsTemplate =
    showTemplateCreationUIFlag || (isOrgAdmin && showOrgTemplateCreationUIFlag);

  const isWorkspaceOwnedByUserOrg =
    selectedMyWorkspaceMetadata?.orgId === userOrgId;

  const saveWorkspaceName = () => {
    if (
      !selectedMyWorkspaceMetadata ||
      !workspaceName ||
      nameError ||
      !nameHasChanged ||
      !isNameEdited
    )
      return;

    dispatch(
      updateWorkspaceName.request({
        workspaceId: selectedMyWorkspaceMetadata?.workspaceId,
        workspaceName,
      }),
    );
    setIsNameEdited(false);
    setWasNameSend(true);
  };

  const showRemoveWorkspacePrompt = () => {
    if (!selectedMyWorkspaceMetadata) return;

    dispatch(
      activateDashboardOption({
        optionType: DashboardOption.REMOVE,
        workspaceId: selectedMyWorkspaceMetadata.workspaceId,
      }),
    );
  };

  const toggleOnHeaderClick = () => {
    setIsOpenSection((prevState) => !prevState);
  };

  useEffect(() => {
    if (!nameHasChanged) return;

    const handler = setTimeout(() => {
      saveWorkspaceName();
    }, 500);

    return () => clearTimeout(handler);
  }, [workspaceName, nameHasChanged]);

  const titleInputLabel = useMemo(() => {
    let label = t("WORKSPACE_TITLE");

    if (nameHasChanged && isNameEdited) {
      label += ` - ${t("SAVING")}`;
    }

    if (!nameHasChanged && wasNameSend) {
      label += ` - ${t("SAVED")}`;
    }

    return label;
  }, [nameHasChanged, wasNameSend]);

  const handleTitleInputBlur = () => {
    setTimeout(() => {
      setWasNameSend(false);
    }, 3000);
  };

  const handleKeyUp = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event?.key === "Enter") {
      saveWorkspaceName();
    }
  };

  useEffect(() => {
    if (shouldSelectTitle) {
      titleInputRef.current?.select();
    }
  }, []);

  const handleNameChange = (value: string | undefined) => {
    setWorkspaceName(value);
    setIsNameEdited(true);
  };

  return (
    <ShareSection testId="workspace-info">
      <ShareSectionHeader
        alwaysShowIcons
        isSectionVisible={isOpenSection}
        onHeaderClick={toggleOnHeaderClick}
        sectionIcon="hoylu-ui-icons-blank"
        sectionTitle={t(
          "WORKSPACE_INFO",
          selectedMyWorkspaceMetadata?.workspaceName,
        )}
      />
      {isOpenSection && !!selectedMyWorkspaceMetadata && (
        <div className={Styles.workspaceInfoSection}>
          <div className={Styles.workspaceInfoEditContainer}>
            <SimpleWorkspaceCard
              myWorkspaceMetadata={selectedMyWorkspaceMetadata}
            >
              {isWorkspaceAdmin ? (
                <div
                  className={Styles.workspaceCard}
                  onClick={() => dispatch(editThumbnail({ flag: true }))}
                >
                  <span className="hoylu-ui-icons-edit" />
                  <span>{t("CHANGE_THUMBNAIL")}</span>
                </div>
              ) : (
                <span
                  className={Styles.truncate}
                  title={selectedMyWorkspaceMetadata.workspaceName ?? ""}
                >
                  {selectedMyWorkspaceMetadata.workspaceName}
                </span>
              )}
            </SimpleWorkspaceCard>
            <div className={Styles.infoContainer}>
              <div style={{ display: "flex" }}>
                <TextInput
                  disabled={!isWorkspaceAdmin}
                  error={nameError}
                  isFocus={shouldSelectTitle}
                  label={titleInputLabel}
                  onBlur={handleTitleInputBlur}
                  onChange={handleNameChange}
                  onKeyUp={handleKeyUp}
                  // This will require change on client-common package to allow null values.
                  parentRef={titleInputRef as React.RefObject<HTMLInputElement>}
                  style={{ flex: 1 }}
                  value={workspaceName}
                />
              </div>
              <ProjectDropdownWithRemoveButton
                currentProjects={currentProjects}
                isEditing
                projectOptions={projectOptions}
                setCurrentProjects={setCurrentProjects}
                setDropdownStateRef={setDropdownStateRef}
              />
            </div>
          </div>
          <div className={Styles.footer}>
            <RemoveButton
              onClick={showRemoveWorkspacePrompt}
              text={t("REMOVE_WORKSPACE")}
            />
            {isWorkspaceAdmin && (
              <div>
                {showPublishAsTemplate && (
                  <Button
                    disabled={!isWorkspaceOwnedByUserOrg}
                    onClick={() =>
                      handleChangeSectionType(InfoSection.TEMPLATE)
                    }
                    toolTip={
                      !isWorkspaceOwnedByUserOrg
                        ? t("PUBLISH_AS_TEMPLATE_NOT_ALLOWED")
                        : t("PUBLISH_AS_TEMPLATE")
                    }
                    type="secondary"
                  >
                    {t("PUBLISH_AS_TEMPLATE")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </ShareSection>
  );
};
